import {
  Grid,
  IconButton,
  Menu,
  Typography,
  styled,
  MenuItem,
  CircularProgress
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import DriveFileRenameOutlineOutlinedIcon from '@mui/icons-material/DriveFileRenameOutlineOutlined';
import { useAppSelector, useMenu } from 'src/hooks';
import './index.scss';
import { useState } from 'react';
import moment from 'moment';
import { IProductComment } from '../../types/ppg-sku-drawer.model';
import { IModuleAccessConfigMap } from '../../../common/types/access-configuration.model';

interface ICommentItemProps {
  comment: IProductComment;
  onEdit: () => void;
  onDelete: () => Promise<unknown>;
  // eslint-disable-next-line react/require-default-props
  isEditable?: boolean;
}

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  padding: theme.spacing(0.5, 1),
  fontSize: '12px'
}));

const isCommentEditDisabled = (
  commentOwnerId: number,
  loggedInUserId: number,
  plannerAccessConfig: IModuleAccessConfigMap['planner']
) => {
  if (commentOwnerId === loggedInUserId) {
    return !plannerAccessConfig.edit_own_comment;
  }
  return !plannerAccessConfig.edit_others_comment;
};

const isCommentDeleteDisabled = (
  commentOwnerId: number,
  loggedInUserId: number,
  plannerAccessConfig: IModuleAccessConfigMap['planner']
) => {
  if (commentOwnerId === loggedInUserId) {
    return !plannerAccessConfig.delete_own_comment;
  }
  return !plannerAccessConfig.delete_others_comment;
};

export default function CommentItem({
  comment,
  onEdit,
  onDelete,
  isEditable = true
}: ICommentItemProps) {
  const { planner: plannerAccessConfig } = useAppSelector(
    (state) => state.simulatorHome.moduleAccessMap
  );
  const user = useAppSelector((state) => state.user);
  const { isMenuOpen, anchorEl, handleMenuOpen, handleMenuClose } = useMenu();
  const [isLoading, setIsLoading] = useState(false);
  return (
    <Grid container justifyContent='space-between' alignItems='center'>
      <Grid item xs={!isEditable ? 12 : true}>
        <Grid container item xs={12} alignItems='baseline'>
          <Typography fontSize='12px' fontWeight={700}>
            {comment.comment_user_name}
          </Typography>
          <Typography fontSize='10px' ml={1} color='#656565' fontWeight={700}>
            {moment(comment.updated_at).format('DD/MM/YYYY h:mm A')}
          </Typography>
        </Grid>
        <Grid item xs={12} mt={0.5}>
          <Typography
            textAlign='initial'
            fontSize='12px'
            whiteSpace='pre-wrap'
            sx={{ wordBreak: 'break-word' }}
          >
            {comment.comment_text}
          </Typography>
        </Grid>
      </Grid>
      {isEditable ? (
        <Grid item xs='auto'>
          {isLoading ? (
            <CircularProgress
              size='small'
              sx={{ height: '10px', width: '10px' }}
            />
          ) : (
            <IconButton
              aria-label='MoreVert'
              sx={{ fontSize: '16px' }}
              onClick={handleMenuOpen}
            >
              <MoreVertIcon fontSize='inherit' />
            </IconButton>
          )}

          {isMenuOpen && (
            <Menu
              id='basic-menu'
              anchorEl={anchorEl}
              open={isMenuOpen}
              onClose={handleMenuClose}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'left'
              }}
              transformOrigin={{
                vertical: 'center',
                horizontal: 'right'
              }}
              MenuListProps={{
                'aria-labelledby': 'basic-button'
              }}
              sx={{ padding: '4px 0 4px 0' }}
            >
              <StyledMenuItem
                disabled={isCommentEditDisabled(
                  comment.comment_user,
                  user?.user,
                  plannerAccessConfig
                )}
                onClick={onEdit}
              >
                <DriveFileRenameOutlineOutlinedIcon
                  fontSize='inherit'
                  color='primary'
                  sx={{ marginRight: '4px' }}
                />
                Edit Comment
              </StyledMenuItem>
              <StyledMenuItem
                disabled={isCommentDeleteDisabled(
                  comment.comment_user,
                  user?.user,
                  plannerAccessConfig
                )}
                onClick={() => {
                  handleMenuClose();
                  onDelete().finally(() => setIsLoading(false));
                }}
              >
                <DeleteOutlineOutlinedIcon
                  fontSize='inherit'
                  color='primary'
                  sx={{ marginRight: '4px' }}
                />
                Delete Comment
              </StyledMenuItem>
            </Menu>
          )}
        </Grid>
      ) : null}
    </Grid>
  );
}
